import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FlaskConical,
  Loader2,
  ShieldCheck,
} from 'lucide-react';

import { useLanguage } from 'shared/context/LanguageContext';
import { ltrIsolate } from 'shared/lib/bidi';

interface OtpStepProps {
  sentTo: string;
  expiresIn: number;
  isVerifying: boolean;
  isSuccess: boolean;
  errorMessage: string | null;
  devOtp?: string | null;
  backLabel?: string;
  onBack: () => void;
  onSubmit: (code: string) => void;
  onResend: () => Promise<void> | void;
}

const OTP_LENGTH = 6;

const OtpStep: React.FC<OtpStepProps> = ({
  sentTo,
  expiresIn,
  isVerifying,
  isSuccess,
  errorMessage,
  devOtp,
  backLabel,
  onBack,
  onSubmit,
  onResend,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const Back = dir === 'rtl' ? ArrowRight : ArrowLeft;

  const [digits, setDigits] = useState<string[]>(() =>
    Array(OTP_LENGTH).fill(''),
  );
  const [countdown, setCountdown] = useState<number>(expiresIn);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setCountdown(expiresIn);
  }, [expiresIn]);

  useEffect(() => {
    if (countdown <= 0) return;
    const id = window.setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearInterval(id);
  }, [countdown]);

  const code = useMemo(() => digits.join(''), [digits]);
  const canSubmit = code.length === OTP_LENGTH && !isVerifying && !isSuccess;
  const canResend = countdown <= 0 && !isResending && !isSuccess;

  useEffect(() => {
    if (canSubmit) {
      onSubmit(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleDigit = (idx: number, value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = cleaned;
      return next;
    });
    if (cleaned && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => {
      next[i] = ch;
    });
    setDigits(next);
    const lastIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[lastIdx]?.focus();
  };

  const handleAutofill = () => {
    if (!devOtp) return;
    const cleaned = devOtp.replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (cleaned.length !== OTP_LENGTH) return;
    setDigits(cleaned.split(''));
    inputRefs.current[OTP_LENGTH - 1]?.focus();
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      setIsResending(true);
      await onResend();
    } finally {
      setIsResending(false);
    }
  };

  const minutes = Math.floor(countdown / 60);
  const seconds = (countdown % 60).toString().padStart(2, '0');

  return (
    <div className="space-y-5">
      <div className="text-center mb-1">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-haat-red-50 flex items-center justify-center mb-3">
          <ShieldCheck className="h-7 w-7 text-haat-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {t('partnerAuth.otp.heading')}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('partnerAuth.otp.sentTo', { phone: ltrIsolate(sentTo) })}
        </p>
      </div>

      {devOtp && !isSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-200/70 text-amber-800">
            <FlaskConical className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
              DEV / SMS stub mode
            </div>
            <div className="font-mono text-lg font-bold text-amber-900 tracking-[0.35em]" dir="ltr">
              {devOtp}
            </div>
          </div>
          <button
            type="button"
            onClick={handleAutofill}
            disabled={isVerifying}
            className="shrink-0 rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            Autofill
          </button>
        </motion.div>
      )}

      <div className="flex justify-center gap-2" dir="ltr">
        {digits.map((digit, idx) => (
          <input
            key={idx}
            ref={(el) => {
              inputRefs.current[idx] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            disabled={isVerifying || isSuccess}
            onChange={(e) => handleDigit(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={handlePaste}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-colors ${
              isSuccess
                ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                : digit
                ? 'border-haat-red-500 bg-white text-gray-900'
                : 'border-gray-200 bg-gray-50 focus:border-haat-red-500'
            }`}
          />
        ))}
      </div>

      {errorMessage && !isSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700 text-center"
        >
          {errorMessage}
        </motion.div>
      )}

      {isSuccess && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center justify-center gap-2 text-emerald-700 font-semibold"
        >
          <CheckCircle2 className="h-5 w-5" />
          {t('partnerAuth.otp.success')}
        </motion.div>
      )}

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={handleResend}
          disabled={!canResend}
          className="font-semibold text-haat-red-600 hover:text-haat-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {isResending ? t('common.loading') : t('partnerAuth.otp.resend')}
        </button>
        <span className="text-gray-500 tabular-nums">
          {countdown > 0
            ? `${minutes}:${seconds}`
            : t('partnerAuth.otp.expired')}
        </span>
      </div>

      <button
        type="button"
        onClick={onBack}
        disabled={isVerifying || isSuccess}
        className="w-full rounded-2xl border-2 border-gray-200 bg-white text-gray-700 font-semibold py-3 flex items-center justify-center gap-2 hover:bg-gray-50 disabled:opacity-50"
      >
        <Back className="h-4 w-4" />
        {backLabel ?? t('partnerAuth.otp.changeChannel')}
      </button>

      {isVerifying && !isSuccess && (
        <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('partnerAuth.otp.verifying')}
        </div>
      )}
    </div>
  );
};

export default OtpStep;
